/**
 * FLAG VILLAGE — Complete Shopify Theme JavaScript v6.0
 * Features: Hero Slider, Sticky Nav, Mobile Menu, FLAGGY AI Bot,
 *           Cookie Banner, Cart AJAX, Product Gallery, Scroll Animations,
 *           Shipping Progress, Quantity Selector, Variant Selector
 */

'use strict';

/* ═══════════════════════════════════════════════
   HERO SLIDER
═══════════════════════════════════════════════ */
class FVHeroSlider {
  constructor(el) {
    this.el = el;
    this.slides = Array.from(el.querySelectorAll('.fv-hero__slide'));
    this.dots = Array.from(el.querySelectorAll('.fv-hero__dot'));
    this.current = 0;
    this.total = this.slides.length;
    this.timer = null;
    if (this.total < 2) { if (this.total === 1) this.slides[0].classList.add('active'); return; }
    this.init();
  }
  init() {
    this.show(0);
    this.startAuto();
    this.el.querySelector('.fv-hero__prev')?.addEventListener('click', () => { this.prev(); this.resetAuto(); });
    this.el.querySelector('.fv-hero__next')?.addEventListener('click', () => { this.next(); this.resetAuto(); });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.show(i); this.resetAuto(); }));
    // Touch support
    let startX = 0;
    this.el.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.el.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? this.next() : this.prev(); this.resetAuto(); }
    });
  }
  show(index) {
    this.slides[this.current]?.classList.remove('active');
    this.dots[this.current]?.classList.remove('active');
    this.current = ((index % this.total) + this.total) % this.total;
    this.slides[this.current]?.classList.add('active');
    this.dots[this.current]?.classList.add('active');
  }
  next() { this.show(this.current + 1); }
  prev() { this.show(this.current - 1); }
  startAuto() { this.timer = setInterval(() => this.next(), 6000); }
  resetAuto() { clearInterval(this.timer); this.startAuto(); }
}

/* ═══════════════════════════════════════════════
   STICKY HEADER
═══════════════════════════════════════════════ */
class FVStickyHeader {
  constructor() {
    this.header = document.querySelector('.fv-header');
    if (!this.header) return;
    window.addEventListener('scroll', () => {
      this.header.classList.toggle('scrolled', window.scrollY > 30);
    }, { passive: true });
  }
}

/* ═══════════════════════════════════════════════
   MOBILE MENU
═══════════════════════════════════════════════ */
class FVMobileMenu {
  constructor() {
    this.menu = document.getElementById('fv-mobile-menu');
    this.overlay = document.getElementById('fv-mobile-overlay');
    this.openBtn = document.getElementById('fv-menu-open');
    this.closeBtn = document.getElementById('fv-menu-close');
    if (!this.menu) return;
    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }
  open() { this.menu.classList.add('open'); this.overlay?.classList.add('open'); document.body.style.overflow = 'hidden'; }
  close() { this.menu.classList.remove('open'); this.overlay?.classList.remove('open'); document.body.style.overflow = ''; }
}

/* ═══════════════════════════════════════════════
   PRODUCT GALLERY
═══════════════════════════════════════════════ */
class FVProductGallery {
  constructor(el) {
    this.main = el.querySelector('.fv-product-gallery__main img');
    this.thumbs = Array.from(el.querySelectorAll('.fv-product-gallery__thumb'));
    if (!this.main || !this.thumbs.length) return;
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const src = thumb.querySelector('img')?.src;
        if (src) { this.main.src = src; this.thumbs.forEach(t => t.classList.remove('active')); thumb.classList.add('active'); }
      });
    });
    if (this.thumbs[0]) this.thumbs[0].classList.add('active');
  }
}

/* ═══════════════════════════════════════════════
   VARIANT SELECTOR
═══════════════════════════════════════════════ */
class FVVariantSelector {
  constructor(el) {
    this.form = el;
    this.variantBtns = Array.from(el.querySelectorAll('.fv-variant-btn'));
    this.addToCartBtn = el.querySelector('.fv-add-to-cart');
    this.priceEl = el.querySelector('.fv-product-info__price');
    this.variantInput = el.querySelector('input[name="id"]');
    this.variantData = JSON.parse(el.dataset.variants || '[]');
    this.selectedOptions = {};
    this.init();
  }
  init() {
    this.variantBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const option = btn.dataset.option;
        const value = btn.dataset.value;
        // Deselect siblings
        this.variantBtns.filter(b => b.dataset.option === option).forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedOptions[option] = value;
        this.updateVariant();
      });
    });
    // Auto-select first of each option
    const options = [...new Set(this.variantBtns.map(b => b.dataset.option))];
    options.forEach(opt => {
      const first = this.variantBtns.find(b => b.dataset.option === opt && !b.classList.contains('sold-out'));
      if (first) { first.classList.add('active'); this.selectedOptions[opt] = first.dataset.value; }
    });
    this.updateVariant();
  }
  updateVariant() {
    const variant = this.variantData.find(v => {
      return Object.entries(this.selectedOptions).every(([opt, val]) => v.options && v.options.includes(val));
    }) || this.variantData[0];
    if (!variant) return;
    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = this.formatPrice(variant.price);
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !variant.available;
      this.addToCartBtn.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
    }
  }
  formatPrice(cents) { return '$' + (cents / 100).toFixed(2); }
}

/* ═══════════════════════════════════════════════
   QUANTITY SELECTOR
═══════════════════════════════════════════════ */
class FVQtySelector {
  constructor(el) {
    this.el = el;
    this.input = el.querySelector('.fv-qty-input');
    el.querySelector('.fv-qty-minus')?.addEventListener('click', () => this.change(-1));
    el.querySelector('.fv-qty-plus')?.addEventListener('click', () => this.change(1));
  }
  change(delta) {
    const val = parseInt(this.input.value) || 1;
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || 99;
    this.input.value = Math.max(min, Math.min(max, val + delta));
  }
}

/* ═══════════════════════════════════════════════
   AJAX ADD TO CART
═══════════════════════════════════════════════ */
class FVAddToCart {
  constructor(form) {
    this.form = form;
    this.btn = form.querySelector('.fv-add-to-cart');
    form.addEventListener('submit', e => { e.preventDefault(); this.submit(); });
  }
  async submit() {
    if (!this.btn || this.btn.disabled) return;
    const variantId = this.form.querySelector('input[name="id"]')?.value;
    const qty = parseInt(this.form.querySelector('.fv-qty-input')?.value) || 1;
    if (!variantId) return;
    this.btn.textContent = 'ADDING...';
    this.btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });
      if (res.ok) {
        this.btn.textContent = '✓ ADDED!';
        this.btn.style.background = '#22c55e';
        FVCart.updateCount();
        setTimeout(() => {
          this.btn.textContent = 'ADD TO CART';
          this.btn.style.background = '';
          this.btn.disabled = false;
        }, 2000);
      } else {
        throw new Error('Add to cart failed');
      }
    } catch (err) {
      this.btn.textContent = 'TRY AGAIN';
      this.btn.disabled = false;
    }
  }
}

/* ═══════════════════════════════════════════════
   CART COUNT & SHIPPING PROGRESS
═══════════════════════════════════════════════ */
const FVCart = {
  threshold: 30000, // $300 in cents
  async updateCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      // Update count badges
      document.querySelectorAll('.fv-cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      // Update shipping progress
      this.updateProgress(cart.total_price);
    } catch (e) {}
  },
  updateProgress(totalCents) {
    const bar = document.querySelector('.fv-shipping-progress__fill');
    const text = document.querySelector('.fv-shipping-progress__text');
    if (!bar && !text) return;
    const pct = Math.min((totalCents / this.threshold) * 100, 100);
    const remaining = Math.max((this.threshold - totalCents) / 100, 0);
    if (bar) bar.style.width = pct + '%';
    if (text) {
      text.textContent = remaining > 0
        ? `Add $${remaining.toFixed(2)} more for FREE shipping 🚚`
        : '🎉 You\'ve unlocked FREE shipping!';
    }
  }
};

/* ═══════════════════════════════════════════════
   QUICK ADD TO CART (Collection/Home)
═══════════════════════════════════════════════ */
async function fvQuickAdd(variantId, btn) {
  if (!variantId) return;
  const origText = btn.textContent;
  btn.textContent = '...';
  btn.disabled = true;
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (res.ok) {
      btn.textContent = '✓';
      btn.style.background = '#22c55e';
      FVCart.updateCount();
      setTimeout(() => { btn.textContent = origText; btn.style.background = ''; btn.disabled = false; }, 1800);
    } else { throw new Error(); }
  } catch { btn.textContent = origText; btn.disabled = false; }
}

/* ═══════════════════════════════════════════════
   CART ITEM REMOVE
═══════════════════════════════════════════════ */
async function fvRemoveCartItem(key, row) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    if (res.ok) { row?.remove(); FVCart.updateCount(); }
  } catch (e) {}
}

/* ═══════════════════════════════════════════════
   FLAGGY AI BOT
═══════════════════════════════════════════════ */
class FVFlaggyBot {
  constructor() {
    this.btn = document.getElementById('fv-bot-btn');
    this.window = document.getElementById('fv-bot-window');
    this.closeBtn = document.getElementById('fv-bot-close');
    this.messages = document.getElementById('fv-bot-messages');
    this.input = document.getElementById('fv-bot-input');
    this.sendBtn = document.getElementById('fv-bot-send');
    if (!this.btn) return;
    this.btn.addEventListener('click', () => this.toggle());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.sendBtn?.addEventListener('click', () => this.send());
    this.input?.addEventListener('keydown', e => { if (e.key === 'Enter') this.send(); });
    document.querySelectorAll('.fv-bot-quick').forEach(b => {
      b.addEventListener('click', () => this.processMsg(b.textContent.trim()));
    });
  }
  toggle() {
    if (!this.window) return;
    const isOpen = this.window.classList.toggle('open');
    if (isOpen) { document.getElementById('fv-bot-badge')?.remove(); this.input?.focus(); }
  }
  close() { this.window?.classList.remove('open'); }
  send() {
    const t = this.input?.value?.trim();
    if (!t) return;
    this.processMsg(t);
    if (this.input) this.input.value = '';
  }
  processMsg(text) {
    this.addMsg(text, 'user');
    this.showTyping();
    setTimeout(() => { this.hideTyping(); this.addMsg(this.getReply(text), 'bot'); }, 600 + Math.random() * 500);
  }
  addMsg(text, role) {
    if (!this.messages) return;
    const wrap = document.createElement('div');
    wrap.className = `fv-bot-msg fv-bot-msg--${role}`;
    if (role === 'bot') {
      const logo = document.createElement('img');
      logo.src = document.querySelector('.fv-header__logo img')?.src || '';
      logo.className = 'fv-bot-msg__bot-logo';
      wrap.appendChild(logo);
    }
    const bubble = document.createElement('div');
    bubble.className = 'fv-bot-msg__bubble';
    bubble.textContent = text;
    wrap.appendChild(bubble);
    this.messages.appendChild(wrap);
    this.messages.scrollTop = this.messages.scrollHeight;
  }
  showTyping() {
    const wrap = document.createElement('div');
    wrap.className = 'fv-bot-msg fv-bot-msg--bot';
    wrap.id = 'fv-bot-typing';
    const logo = document.createElement('img');
    logo.src = document.querySelector('.fv-header__logo img')?.src || '';
    logo.className = 'fv-bot-msg__bot-logo';
    wrap.appendChild(logo);
    const typing = document.createElement('div');
    typing.className = 'fv-bot-typing';
    typing.innerHTML = '<div class="fv-bot-typing__dots"><div class="fv-bot-typing__dot"></div><div class="fv-bot-typing__dot"></div><div class="fv-bot-typing__dot"></div></div>';
    wrap.appendChild(typing);
    this.messages?.appendChild(wrap);
    this.messages.scrollTop = this.messages.scrollHeight;
  }
  hideTyping() { document.getElementById('fv-bot-typing')?.remove(); }
  getReply(t) {
    const l = t.toLowerCase();
    if (l.match(/^(hi|hello|hey|good|howdy)/)) return 'Welcome to FLAG VILLAGE! 🏁 I\'m FLAGGY, your AI guide.\n\nI can help with:\n• Products (caps, mugs, patches, puzzles, blankets)\n• Shipping & returns policies\n• Village Rewards program\n• B2B wholesale\n\nWhat can I help you with?';
    if (l.includes('cap') || l.includes('hat')) return '🧢 CAPS & HATS\n\nPremium leather patch caps — 340+ flag designs at $20.45 each.\n\nAll 50 USA states + 195 nations available.\n\nShop: /collections/cap-hat';
    if (l.includes('mug') || l.includes('coffee')) return '☕ SPORTING FLAG MUGS\n\nCeramic coffee mugs (11oz) with unique athletic flag artwork.\n186+ countries · $19.99 each · Dishwasher safe.\n\nShop: /collections/mugs';
    if (l.includes('patch') || l.includes('embroid')) return '🪡 EMBROIDERY PATCHES\n\nPrecision iron-on & sew-on flag patches.\n225+ countries · $12.37 each.\n\nShop: /collections/international-patch';
    if (l.includes('puzzle')) return '🧩 FLAG PUZZLES\n\nRacing Car & Sporting Flag puzzles.\n110, 252, 520, 1014 pieces · $19.53 each.\n\nShop: /collections/puzzle';
    if (l.includes('blanket')) return '🛏️ FLAG BLANKETS\n\nPremium soft fleece with bold sporting flag designs.\n\nShop: /collections/blanket';
    if (l.includes('t-shirt') || l.includes('tshirt') || l.includes('shirt')) return '👕 SPORTING FLAG T-SHIRTS\n\nHeavy cotton tees with flag designs.\n119+ designs · From $18.80.\n\nShop: /collections/t-shirt';
    if (l.includes('reward') || l.includes('point') || l.includes('tier')) return '⭐ VILLAGE REWARDS\n\n🥈 ROOKIE — 1× points ($0-$299/yr)\n🥇 RACER — 1.5× points ($300-$999/yr)\n👑 RACER — 2× points ($1,000+/yr)\n\n300 points = $1 reward credit\nJoin free: /pages/join';
    if (l.includes('ship') || l.includes('deliver') || l.includes('free shipping')) return '📦 SHIPPING\n\n✅ FREE shipping on orders $300+\n📦 Under $300: calculated at checkout\n⏰ Orders before 2PM ship same day (Mon-Fri)\n\nFull policy: /pages/shipping-policy';
    if (l.includes('return') || l.includes('refund') || l.includes('exchange')) return '↩️ RETURNS\n\nNo-return policy as general rule.\nQuality defects accepted on case-by-case basis.\n\nTo claim: email flagvillage@gmail.com with photos + order number.\n\nFull policy: /pages/returns-policy';
    if (l.includes('warrant')) return '🛡️ WARRANTY\n\n6-month warranty on manufacturing defects.\n\nClaim: email flagvillage@gmail.com within warranty period.\n\nFull details: /pages/warranty';
    if (l.includes('b2b') || l.includes('wholesale') || l.includes('bulk')) return '🏢 B2B WHOLESALE\n\n50-99 units: 10% off\n100-499 units: 18% off\n500+ units: 25% off\n1,000+ units: Custom pricing\n\nInquire: /pages/contact';
    if (l.includes('usa') || l.includes('state') || l.includes('america')) return '🇺🇸 USA COLLECTION\n\nAll 50 states + territories!\nCaps, mugs, patches, puzzles, pennants.\n\nBrowse: /collections/cap-hat?sort_by=best-selling';
    if (l.includes('discount') || l.includes('coupon') || l.includes('promo')) return '🎁 CURRENT OFFERS\n\n• New members: Use WELCOME10 for 10% off first order\n• Free shipping on orders $300+\n• Earn rewards on every purchase\n\nJoin: /pages/join';
    if (l.includes('contact') || l.includes('email') || l.includes('support')) return '📬 CONTACT US\n\nEmail: flagvillage@gmail.com\nResponse within 24 business hours.\n\nContact form: /pages/contact';
    return 'I\'d love to help! 🏁\n\nAsk me about:\n• Products (caps, mugs, patches, puzzles)\n• Shipping & returns\n• Village Rewards\n• USA state flags\n• B2B wholesale';
  }
}

/* ═══════════════════════════════════════════════
   COOKIE BANNER
═══════════════════════════════════════════════ */
class FVCookieBanner {
  constructor() {
    this.KEY = 'fv_cookie_v1';
    this.banner = document.getElementById('fv-cookie-banner');
    if (!this.banner || localStorage.getItem(this.KEY)) return;
    setTimeout(() => this.banner.classList.add('visible'), 1200);
    document.getElementById('fv-cookie-accept')?.addEventListener('click', () => this.save({ all: true }));
    document.getElementById('fv-cookie-reject')?.addEventListener('click', () => this.save({ all: false }));
    document.getElementById('fv-cookie-manage')?.addEventListener('click', () => {
      const d = document.getElementById('fv-cookie-details');
      if (d) d.classList.toggle('open');
    });
    document.getElementById('fv-cookie-save-prefs')?.addEventListener('click', () => {
      this.save({
        analytics: document.getElementById('fv-pref-analytics')?.checked || false,
        marketing: document.getElementById('fv-pref-marketing')?.checked || false,
        preferences: document.getElementById('fv-pref-preferences')?.checked !== false,
      });
    });
    // Toggle visual state for checkboxes
    document.querySelectorAll('.fv-toggle input').forEach(cb => {
      cb.addEventListener('change', function() {
        const track = this.nextElementSibling;
        const thumb = track?.nextElementSibling;
        if (track) track.style.background = this.checked ? 'var(--fv-gold)' : 'rgba(255,255,255,0.15)';
        if (thumb) thumb.style.left = this.checked ? '20px' : '4px';
      });
    });
  }
  save(prefs) {
    localStorage.setItem(this.KEY, JSON.stringify({ ...prefs, ts: Date.now() }));
    this.banner.classList.remove('visible');
    if ((prefs.all || prefs.analytics) && window.gtag) {
      window.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: (prefs.all || prefs.marketing) ? 'granted' : 'denied' });
    }
  }
}

/* ═══════════════════════════════════════════════
   SCROLL ANIMATIONS
═══════════════════════════════════════════════ */
class FVScrollAnimations {
  constructor() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.fv-animate, .fv-animate-left').forEach(el => el.classList.add('visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('visible'), i * 60);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.fv-animate, .fv-animate-left').forEach(el => observer.observe(el));
  }
}

/* ═══════════════════════════════════════════════
   COLLECTION FILTERS
═══════════════════════════════════════════════ */
class FVCollectionFilters {
  constructor() {
    document.querySelectorAll('.fv-filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.fv-collection-filters');
        group?.querySelectorAll('.fv-filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        // Trigger Shopify collection filtering via URL
        const tag = btn.dataset.tag;
        if (tag) {
          const url = new URL(window.location.href);
          if (tag === 'all') { url.searchParams.delete('filter.p.tag'); }
          else { url.searchParams.set('filter.p.tag', tag); }
          window.location.href = url.toString();
        }
      });
    });
    // Sort
    document.querySelector('.fv-sort-select')?.addEventListener('change', function() {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }
}

/* ═══════════════════════════════════════════════
   EMAIL POPUP
═══════════════════════════════════════════════ */
class FVEmailPopup {
  constructor() {
    this.KEY = 'fv_popup_v1';
    this.popup = document.getElementById('fv-email-popup');
    if (!this.popup || sessionStorage.getItem(this.KEY)) return;
    setTimeout(() => { this.popup.style.display = 'flex'; }, 5000);
    document.getElementById('fv-popup-close')?.addEventListener('click', () => this.dismiss());
    document.getElementById('fv-popup-backdrop')?.addEventListener('click', () => this.dismiss());
    document.getElementById('fv-popup-form')?.addEventListener('submit', e => {
      e.preventDefault();
      const email = document.getElementById('fv-popup-email')?.value;
      if (email) { this.submitted(email); }
    });
  }
  dismiss() { this.popup.style.display = 'none'; sessionStorage.setItem(this.KEY, '1'); }
  submitted(email) {
    const form = document.getElementById('fv-popup-form-wrap');
    const success = document.getElementById('fv-popup-success');
    if (form) form.style.display = 'none';
    if (success) success.style.display = 'block';
    sessionStorage.setItem(this.KEY, '1');
    setTimeout(() => this.dismiss(), 3000);
  }
}

/* ═══════════════════════════════════════════════
   INIT
═══════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  // Hero
  const heroEl = document.querySelector('.fv-hero');
  if (heroEl) new FVHeroSlider(heroEl);

  // Navigation
  new FVStickyHeader();
  new FVMobileMenu();

  // Product page
  const galleryEl = document.querySelector('.fv-product-gallery');
  if (galleryEl) new FVProductGallery(galleryEl);

  const productForm = document.querySelector('.fv-product-form');
  if (productForm) {
    new FVVariantSelector(productForm);
    new FVAddToCart(productForm);
  }

  document.querySelectorAll('.fv-qty-selector').forEach(el => new FVQtySelector(el));

  // Quick add buttons
  document.querySelectorAll('.fv-product-card__quick-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      fvQuickAdd(btn.dataset.variantId, btn);
    });
  });

  // Cart remove
  document.querySelectorAll('.fv-cart-item__remove').forEach(btn => {
    btn.addEventListener('click', () => fvRemoveCartItem(btn.dataset.key, btn.closest('.fv-cart-item')));
  });

  // Cart count + shipping progress
  FVCart.updateCount();

  // Bot, Cookie, Animations
  new FVFlaggyBot();
  new FVCookieBanner();
  new FVScrollAnimations();
  new FVCollectionFilters();
  new FVEmailPopup();
});
